/**
 * sellerCurrency — dashboard-only currency context for the seller area.
 *
 * The platform's canonical price unit is USD. Sellers see their dashboard
 * money (revenue, payouts, order totals, product prices) in their own
 * currency — defaulted from their saved billing country and overridable
 * via a picker in the dashboard header. Display only: no backend math and
 * no change to how prices are stored or entered (inputs stay in USD).
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { currencyForIso2, fmtCurrency } from "./money";

const KEY = "seller_display_currency";
const SellerCurrencyContext = createContext(null);

export function SellerCurrencyProvider({ defaultCountry, scopeKey, children }) {
  const billingCurrency = currencyForIso2(defaultCountry);
  const storageKey = scopeKey ? `${KEY}:${scopeKey}` : KEY;
  const [override, setOverride] = useState("");

  // Read the saved override once the storage key is known (scopeKey/user may
  // resolve async). Scoping by user avoids one seller inheriting another
  // seller's explicit currency on a shared browser.
  useEffect(() => {
    try { setOverride(localStorage.getItem(storageKey) || ""); } catch { setOverride(""); }
  }, [storageKey]);

  // If the seller hasn't explicitly picked a currency, follow their billing
  // country as soon as analytics resolves it.
  const currency = override || billingCurrency || "USD";

  const setCurrency = (code) => {
    setOverride(code);
    try {
      if (code) localStorage.setItem(storageKey, code);
      else localStorage.removeItem(storageKey);
    } catch { /* noop */ }
  };

  const value = useMemo(() => ({
    currency,
    billingCurrency,
    isAuto: !override,
    setCurrency,
    fmt: (usd, opts) => fmtCurrency(usd, currency, opts),
  }), [currency, billingCurrency, override]);

  return (
    <SellerCurrencyContext.Provider value={value}>
      {children}
    </SellerCurrencyContext.Provider>
  );
}

export function useSellerCurrency() {
  const ctx = useContext(SellerCurrencyContext);
  // Safe fallback so components never crash if rendered outside the provider.
  if (!ctx) {
    return { currency: "USD", billingCurrency: "USD", isAuto: true, setCurrency: () => {}, fmt: (usd) => fmtCurrency(usd, "USD") };
  }
  return ctx;
}
